import React, { useState, useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  LuTicket,
  LuPackage,
  LuCheck,
  LuList,
  LuUser,
  LuUsers,
  LuDog,
  LuChevronDown,
  LuCheckCircle,
  LuAlertTriangle,
  LuX,
} from 'react-icons/lu';
import { AGE_RANGES } from './DependentForm';
import { planosAPI } from '../services/api';
import api from '../services/api';
import {
  calculateAge,
  getTitularPriceByAge,
  getDependentPriceByAge,
  getPetPrice as getPetPriceShared,
  getExclusivePetPlanPrice,
} from '../utils/pricing';

const iconStyle = { display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' };

const TicketIcon = () => <LuTicket size={18} style={iconStyle} />;
const PackageIcon = () => <LuPackage size={18} style={iconStyle} />;
const CheckIcon = () => <LuCheck size={14} style={iconStyle} />;
const ListIcon = () => <LuList size={18} style={iconStyle} />;
const UserIcon = () => <LuUser size={18} style={iconStyle} />;
const UsersIcon = () => <LuUsers size={18} style={iconStyle} />;
const PawIcon = () => <LuDog size={18} style={iconStyle} />;
const ChevronDownIcon = () => <LuChevronDown size={20} style={iconStyle} />;
const CheckCircleIcon = () => <LuCheckCircle size={16} style={iconStyle} />;

const SummaryBarContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  transition: all 0.3s ease;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
`;

const CollapsedBar = styled.div`
  background: ${props => props.theme.colors.successGreen};
  color: ${props => props.theme.colors.white};
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  @media (max-width: 768px) {
    padding: 12px 15px;
  }
`;

const PriceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  flex: 1;
  
  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const PriceLabel = styled.div`
  font-size: 14px;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const PriceValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const ExpandButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const ExpandIcon = styled.span`
  font-size: 20px;
  transition: transform 0.3s ease;
  transform: ${props => props.$expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const ExpandedContent = styled.div`
  background: ${props => props.theme.colors.white};
  max-height: ${props => props.expanded ? '400px' : '0'};
  overflow-y: auto;
  transition: max-height 0.3s ease;
  border-top: 3px solid ${props => props.theme.colors.successGreen};
  
  @media (max-width: 768px) {
    max-height: ${props => props.expanded ? '300px' : '0'};
  }
`;

const ExpandedInner = styled.div`
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const DetailSection = styled.div`
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: ${props => props.theme.colors.grayLight};
  border-radius: 6px;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    padding: 8px;
  }
`;

const DetailLabel = styled.div`
  font-size: 14px;
  color: ${props => props.theme.colors.grayDark};
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
  
  span {
    font-size: 12px;
    color: ${props => props.theme.colors.grayMedium};
    display: block;
    margin-top: 2px;
    
    @media (max-width: 768px) {
      font-size: 11px;
    }
  }
`;

const DetailValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.colors.successGreen};
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const TotalSection = styled.div`
  margin-top: 15px;
  padding-top: 15px;
  border-top: 2px solid ${props => props.theme.colors.lightBlue};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TotalLabel = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const TotalValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.theme.colors.successGreen};
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 20px;
  color: ${props => props.theme.colors.grayMedium};
  font-size: 14px;
`;

const CouponSection = styled.div`
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px dashed ${props => props.theme.colors.grayMedium};
`;

const CouponTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CouponInputContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
`;

const CouponInput = styled.input`
  flex: 1;
  padding: 10px;
  border: 2px solid ${props => props.theme.colors.grayLight};
  border-radius: 6px;
  font-size: 14px;
  text-transform: uppercase;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const CouponButton = styled.button`
  padding: 10px 20px;
  background: ${props => props.theme.colors.successGreen};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #059669;
  }
  
  &:disabled {
    background: ${props => props.theme.colors.grayMedium};
    cursor: not-allowed;
  }
`;

const RemoveCouponButton = styled.button`
  padding: 10px 20px;
  background: ${props => props.theme.colors.danger};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.9;
  }
`;

const CouponFeedback = styled.div`
  padding: 10px;
  border-radius: 6px;
  font-size: 13px;
  margin-bottom: 10px;
  background: ${props => props.$success ? '#d1fae5' : '#fee2e2'};
  color: ${props => props.$success ? '#065f46' : '#991b1b'};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DiscountItem = styled(DetailItem)`
  background: #d1fae5;
  border: 2px solid ${props => props.theme.colors.successGreen};
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${props => props.theme.colors.grayLight};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e5e7eb;
  }
`;

const CheckboxInput = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: ${props => props.theme.colors.primary};
`;

const CheckboxLabel = styled.label`
  flex: 1;
  font-size: 14px;
  color: ${props => props.theme.colors.grayDark};
  cursor: pointer;
  
  strong {
    color: ${props => props.theme.colors.primary};
    font-weight: 600;
  }
  
  span {
    font-size: 12px;
    color: ${props => props.theme.colors.grayMedium};
    display: block;
    margin-top: 2px;
  }
`;

const FreteValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
`;

const PriceSummaryBar = ({ selectedPlan, holderData, dependents = [], pets = [], appliedCoupon: appliedCouponProp, onCouponApply, receberFisico, onReceberFisicoChange, valorFrete = 15.00 }) => {
  const [expanded, setExpanded] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [pricingError, setPricingError] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(appliedCouponProp || null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState(null);
  const [couponSuccess, setCouponSuccess] = useState(null);
  const summaryBarRef = useRef(null);

  // Avisa o resto da UI (ex.: botão do Ale) que a barra fixa está na tela.
  useEffect(() => {
    document.body.classList.add('has-price-summary-bar');
    return () => document.body.classList.remove('has-price-summary-bar');
  }, []);

  // Sincronizar estado local com a prop appliedCoupon do componente pai
  useEffect(() => {
    if (appliedCouponProp) {
      setAppliedCoupon(appliedCouponProp);
      if (appliedCouponProp.cupom?.codigo) {
        const valorFormatado = appliedCouponProp.valorDesconto.toFixed(2).replace('.', ',');
        setCouponSuccess(`Cupom aplicado! Desconto de R$ ${valorFormatado} na 1ª mensalidade`);
      }
    } else if (appliedCouponProp === null && appliedCoupon !== null) {
      // Se a prop foi limpa, limpar o estado local também
      setAppliedCoupon(null);
      setCouponSuccess(null);
    }
  }, [appliedCouponProp, appliedCoupon]);

  useEffect(() => {
    let mounted = true;
    setPricingError(null);
    
    planosAPI
      .getValores()
      .then((res) => {
        const data = res.data?.data?.pricing || null;
        if (mounted) {
          setPricing(data);
          setPricingError(null);
        }
      })
      .catch((err) => {
        console.error('Erro ao carregar valores de planos:', err);
        if (mounted) {
          setPricingError('Não foi possível carregar os valores dos planos. Por favor, verifique sua conexão e tente novamente.');
        }
      });
    
    return () => {
      mounted = false;
    };
  }, []);

  // Fechar menu ao clicar fora da área
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (expanded && summaryBarRef.current && !summaryBarRef.current.contains(event.target)) {
        setExpanded(false);
      }
    };

    // Adicionar event listener apenas quando o menu estiver expandido
    if (expanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup - remover event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [expanded]);

  const getAgeRangeDescription = (age, isDependent = false) => {
    // Para dependentes: buscar nos tiers de dependentes
    if (isDependent && pricing && pricing.dependentes && selectedPlan) {
      const idPlano = String(selectedPlan.idPlano);
      const dependentesData = pricing.dependentes[idPlano];
      
      if (dependentesData && dependentesData.tiers && Array.isArray(dependentesData.tiers)) {
        const faixa = dependentesData.tiers.find((t) => {
          const de = typeof t.idadeDe === 'number' ? t.idadeDe : -Infinity;
          const ate = typeof t.idadeAte === 'number' ? t.idadeAte : Infinity;
          return age >= de && age <= ate;
        });
        
        if (faixa) {
          const idadeDe = faixa.idadeDe;
          const idadeAte = faixa.idadeAte;
          
          if (idadeAte >= 999 || idadeAte >= 9999) {
            return `Acima de ${idadeDe} anos`;
          } else if (idadeDe === idadeAte) {
            return `${idadeDe} anos`;
          } else {
            return `${idadeDe} a ${idadeAte} anos`;
          }
        }
      }
    }
    
    // Para titular: buscar nos tiers do plano
    if (!isDependent && selectedPlan && selectedPlan.pricingTiers && Array.isArray(selectedPlan.pricingTiers)) {
      const faixa = selectedPlan.pricingTiers.find((t) => {
        const de = typeof t.idadeDe === 'number' ? t.idadeDe : -Infinity;
        const ate = typeof t.idadeAte === 'number' ? t.idadeAte : Infinity;
        return age >= de && age <= ate;
      });
      
      if (faixa) {
        const idadeDe = faixa.idadeDe;
        const idadeAte = faixa.idadeAte;
        
        if (idadeAte >= 999 || idadeAte >= 9999) {
          return `Acima de ${idadeDe} anos`;
        } else if (idadeDe === idadeAte) {
          return `${idadeDe} anos`;
        } else {
          return `${idadeDe} a ${idadeAte} anos`;
        }
      }
    }
    
    // Fallback para valores fixos caso não encontre na API
    if (age <= 18) return AGE_RANGES['0-18'];
    if (age <= 49) return AGE_RANGES['19-49'];
    if (age <= 59) return AGE_RANGES['50-59'];
    if (age <= 69) return AGE_RANGES['60-69'];
    if (age <= 79) return AGE_RANGES['70-79'];
    if (age <= 89) return AGE_RANGES['80-89'];
    return AGE_RANGES['90+'];
  };

  const priceCalculation = useMemo(() => {
    if (!selectedPlan) return null;
    if (pricingError) return null; // Não calcular se houver erro na API

    let breakdown = {
      titular: null,
      dependentes: [],
      pets: []
    };
    let totalPrice = 0;

    // Para planos exclusivos PET, titular não tem custo (valor 0)
    // Apenas os pets são cobrados
    const isExclusivePetPlan = selectedPlan.somentePET === true;

    // Calculate holder price (apenas para planos não-PET)
    let holderPrice = 0;
    let holderAge = null;
    
    if (!isExclusivePetPlan) {
      if (holderData.birthDate) {
        holderAge = calculateAge(holderData.birthDate);
        holderPrice = getTitularPriceByAge(holderAge, selectedPlan);
      } else {
        holderPrice = selectedPlan.pricingTiers?.[0]?.valorReais || selectedPlan.minValue || 0;
      }
      
      if (holderPrice > 0) {
        totalPrice += holderPrice;
        breakdown.titular = {
          label: holderData.fullName || 'Titular',
          age: holderAge,
          ageRange: holderAge ? getAgeRangeDescription(holderAge, false) : 'Preço base do plano',
          price: holderPrice
        };
      }
    }

    // Calculate dependents prices (apenas para planos não-PET)
    if (!isExclusivePetPlan) {
      dependents.forEach((dependent, index) => {
        if (dependent.birthDate) {
          const depAge = calculateAge(dependent.birthDate);
          const depPrice = getDependentPriceByAge(depAge, pricing, selectedPlan);
          
          // Só adicionar se o preço foi encontrado na API
          if (depPrice !== null) {
            totalPrice += depPrice;

            const depName = dependent.name || `Dependente ${index + 1}`;

            breakdown.dependentes.push({
              label: depName,
              age: depAge,
              ageRange: getAgeRangeDescription(depAge, true),
              price: depPrice
            });
          }
        }
      });
    }

    // Calculate pets prices
    pets.forEach((pet, index) => {
      let petPrice = 0;
      let serviceName = '';
      
      // Se for um plano exclusivo para pets, buscar o valor do plano no pricing
      if (isExclusivePetPlan) {
        petPrice = getExclusivePetPlanPrice(pricing, selectedPlan);
        serviceName = selectedPlan.name;
      }
      // Se for um plano normal com pet, usar o valor do serviço selecionado
      else if (pet.service) {
        const petInfo = getPetPriceShared(pet, pricing, selectedPlan);
        petPrice = petInfo.price;
        serviceName = petInfo.desc;
      }

      if (petPrice > 0) {
        const petName = pet.name || `Pet ${index + 1}`;
        
        breakdown.pets.push({
          label: petName,
          ageRange: serviceName,
          price: petPrice
        });
        
        totalPrice += petPrice;
      }
    });

    if (holderPrice > 0 || breakdown.dependentes.length > 0 || breakdown.pets.length > 0) {
      return {
        breakdown,
        totalPrice
      };
    }

    return null;
  }, [selectedPlan, holderData, dependents, pets, pricing]);

  const formatCurrency = (value) => {
    if (!value || isNaN(parseFloat(value))) return '0,00';
    
    try {
      const numValue = parseFloat(value);
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numValue);
    } catch (error) {
      console.warn('Erro ao formatar valor:', value, error);
      return '0,00';
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Digite um código de cupom');
      return;
    }

    // Validar que priceCalculation existe e tem totalPrice
    if (!priceCalculation || priceCalculation.totalPrice === undefined || priceCalculation.totalPrice === null) {
      console.error('Erro: priceCalculation inválido:', priceCalculation);
      setCouponError('Erro: Valor total não disponível. Aguarde o cálculo dos preços.');
      return;
    }

    // Validar que totalPrice é um número válido
    const totalPrice = parseFloat(priceCalculation.totalPrice);
    if (isNaN(totalPrice) || totalPrice <= 0) {
      console.error('Erro: totalPrice inválido:', priceCalculation.totalPrice);
      setCouponError('Erro: Valor total inválido. Verifique os dados do plano.');
      return;
    }

    setCouponLoading(true);
    setCouponError(null);
    setCouponSuccess(null);

    try {
      // Taxa de adesão desabilitada
      const valorTaxaAdesao = 0;
      
      const payload = {
        codigo: couponCode.toUpperCase(),
        valorTotal: totalPrice,
        valorTaxaAdesao: valorTaxaAdesao
      };

      const response = await api.post('/cupons/validar', payload);

      if (response.data.valido) {
        setAppliedCoupon(response.data);
        let msgDesconto = `Cupom aplicado! Desconto de R$ ${formatCurrency(response.data.valorDesconto)} na 1ª mensalidade`;
        setCouponSuccess(msgDesconto);
        setCouponCode('');
        
        // Notificar componente pai
        if (onCouponApply) {
          onCouponApply(response.data);
        }
      }
    } catch (error) {
      console.error('Erro ao validar cupom:', error);
      console.error('API Error:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Cupom inválido ou expirado';
      setCouponError(errorMessage);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponSuccess(null);
    setCouponError(null);
    setCouponCode('');
    
    // Notificar componente pai
    if (onCouponApply) {
      onCouponApply(null);
    }
  };

  // Verificar se há dados necessários antes de calcular
  if (!priceCalculation || !selectedPlan) {
    return null;
  }

  // Calcular preço final incluindo mensalidade (taxa de adesão desabilitada)
  const valorMensalidade = appliedCoupon ? appliedCoupon.valorFinal : priceCalculation.totalPrice;
  const valorTaxaAdesaoFinal = 0;
  const valorFreteCalculado = receberFisico ? valorFrete : 0;
  const descontoCupom = appliedCoupon ? appliedCoupon.valorDesconto : 0;
  
  // Se houver erro na API, mostrar mensagem elegante
  if (pricingError || !priceCalculation) {
    return (
      <SummaryBarContainer ref={summaryBarRef}>
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderLeft: '4px solid #dc2626',
          borderRadius: '8px',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <LuAlertTriangle size={24} color="#dc2626" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: '4px', fontSize: '14px' }}>
              Serviço temporariamente indisponível
            </div>
            <div style={{ color: '#991b1b', fontSize: '13px', lineHeight: 1.5 }}>
              {pricingError || 'Não foi possível carregar os valores dos planos. Por favor, verifique sua conexão e tente novamente.'}
            </div>
          </div>
        </div>
      </SummaryBarContainer>
    );
  }

  // Mensalidade base (sem desconto)
  const mensalidadeBase = priceCalculation.totalPrice;
  
  // Primeira mensalidade (com frete se houver, com desconto se houver)
  const primeiraMensalidade = valorMensalidade + valorFreteCalculado;
  
  // Mensalidade recorrente (sem cupom, sem frete - 2ª mensalidade em diante)
  const mensalidadeRecorrente = mensalidadeBase;

  return (
    <SummaryBarContainer ref={summaryBarRef}>
      <CollapsedBar onClick={() => setExpanded(!expanded)}>
        <PriceInfo>
          <div>
            {(appliedCoupon || valorFreteCalculado > 0) ? (
              <>
                <PriceLabel>1ª Mensalidade</PriceLabel>
                <PriceValue>R$ {formatCurrency(primeiraMensalidade)}</PriceValue>
                <div style={{ fontSize: '11px', color: '#fff', marginTop: '4px' }}>
                  Demais: R$ {formatCurrency(mensalidadeRecorrente)}
                </div>
              </>
            ) : (
              <>
                <PriceLabel>Valor mensal</PriceLabel>
                <PriceValue>R$ {formatCurrency(mensalidadeRecorrente)}</PriceValue>
              </>
            )}
          </div>
        </PriceInfo>
        <ExpandButton>
          Ver detalhes
          <ExpandIcon $expanded={!expanded}><ChevronDownIcon /></ExpandIcon>
        </ExpandButton>
      </CollapsedBar>
      
      <ExpandedContent expanded={expanded}>
        <ExpandedInner>
          <DetailSection>
            <DetailTitle><ListIcon /> {selectedPlan.name}</DetailTitle>
          </DetailSection>

          {priceCalculation.breakdown.titular && (
            <DetailSection>
              <DetailTitle><UserIcon /> Titular</DetailTitle>
              <DetailItem>
                <DetailLabel>
                  <strong>{priceCalculation.breakdown.titular.label}</strong>
                  <span>{priceCalculation.breakdown.titular.ageRange}</span>
                </DetailLabel>
                <DetailValue>R$ {formatCurrency(priceCalculation.breakdown.titular.price)}</DetailValue>
              </DetailItem>
            </DetailSection>
          )}

          {priceCalculation.breakdown.dependentes.length > 0 && (
            <DetailSection>
              <DetailTitle><UsersIcon /> Dependentes ({priceCalculation.breakdown.dependentes.length})</DetailTitle>
              {priceCalculation.breakdown.dependentes.map((dep, index) => (
                <DetailItem key={index}>
                  <DetailLabel>
                    <strong>{dep.label}</strong>
                    <span>{dep.ageRange}</span>
                  </DetailLabel>
                  <DetailValue>R$ {formatCurrency(dep.price)}</DetailValue>
                </DetailItem>
              ))}
            </DetailSection>
          )}

          {priceCalculation.breakdown.pets.length > 0 && (
            <DetailSection>
              <DetailTitle><PawIcon /> Pets ({priceCalculation.breakdown.pets.length})</DetailTitle>
              {priceCalculation.breakdown.pets.map((pet, index) => (
                <DetailItem key={index}>
                  <DetailLabel>
                    <strong>{pet.label}</strong>
                    <span>{pet.ageRange}</span>
                  </DetailLabel>
                  <DetailValue>R$ {formatCurrency(pet.price)}</DetailValue>
                </DetailItem>
              ))}
            </DetailSection>
          )}

          <DetailSection>
            <DetailTitle><PackageIcon /> Entrega de Carnê Físico</DetailTitle>
            <CheckboxContainer onClick={() => onReceberFisicoChange && onReceberFisicoChange(!receberFisico)}>
              <CheckboxInput 
                type="checkbox" 
                id="receber-fisico"
                checked={receberFisico}
                onChange={(e) => onReceberFisicoChange && onReceberFisicoChange(e.target.checked)}
                onClick={(e) => e.stopPropagation()}
              />
              <CheckboxLabel htmlFor="receber-fisico">
                <strong>Desejo receber o carnê físico</strong>
                <span>Carnê de pagamento impresso</span>
              </CheckboxLabel>
              {receberFisico && (
                <FreteValue>
                  {valorFrete > 0
                    ? `R$ ${formatCurrency(valorFrete)} (frete)`
                    : 'Frete grátis'}
                </FreteValue>
              )}
            </CheckboxContainer>
            {!receberFisico && (
              <div style={{ fontSize: '12px', color: '#10b981', marginTop: '8px', padding: '0 12px' }}>
                <CheckIcon /> Você receberá o carnê digitalmente (sem custo adicional)
              </div>
            )}
          </DetailSection>

          <CouponSection>
            <CouponTitle><TicketIcon /> Cupom de Desconto</CouponTitle>
            
            {couponError && (
              <CouponFeedback $success={false}>
                <LuX size={14} style={iconStyle} /> {couponError}
              </CouponFeedback>
            )}
            
            {couponSuccess && (
              <CouponFeedback $success={true}>
                <CheckCircleIcon /> {couponSuccess}
              </CouponFeedback>
            )}
            
            {!appliedCoupon ? (
              <CouponInputContainer>
                <CouponInput
                  type="text"
                  placeholder="Digite o código do cupom"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && handleApplyCoupon()}
                />
                <CouponButton 
                  onClick={handleApplyCoupon}
                  disabled={couponLoading || !couponCode.trim()}
                >
                  {couponLoading ? 'Validando...' : 'Aplicar'}
                </CouponButton>
              </CouponInputContainer>
            ) : (
              <>
                <DiscountItem>
                  <DetailLabel>
                    Cupom: {appliedCoupon.cupom.codigo}
                    <span>Desconto aplicado apenas na 1ª mensalidade</span>
                  </DetailLabel>
                  <DetailValue style={{ color: '#059669' }}>
                    - R$ {formatCurrency(appliedCoupon.valorDesconto)}
                  </DetailValue>
                </DiscountItem>
                <div style={{ marginTop: '10px' }}>
                  <RemoveCouponButton onClick={handleRemoveCoupon}>
                    Remover Cupom
                  </RemoveCouponButton>
                </div>
              </>
            )}
          </CouponSection>

          <TotalSection>
            <div style={{ flex: 1 }}>
              {(appliedCoupon || valorFreteCalculado > 0) ? (
                <>
                  <TotalLabel>1ª Mensalidade</TotalLabel>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '5px' }}>
                    {appliedCoupon && (
                      <div style={{ color: '#059669', fontWeight: '600', marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <LuCheck size={12} /> Desconto do cupom aplicado
                      </div>
                    )}
                    {valorFreteCalculado > 0 && (
                      <div style={{ marginBottom: '3px' }}>
                        + R$ {formatCurrency(valorFrete)} de frete (carnê físico)
                      </div>
                    )}
                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                      <strong>Demais mensalidades:</strong> R$ {formatCurrency(mensalidadeRecorrente)}
                    </div>
                  </div>
                </>
              ) : (
                <TotalLabel>Total Mensal</TotalLabel>
              )}
            </div>
            <TotalValue>
              {(appliedCoupon || valorFreteCalculado > 0) ? (
                <>R$ {formatCurrency(primeiraMensalidade)}</>
              ) : (
                <>R$ {formatCurrency(mensalidadeRecorrente)}</>
              )}
            </TotalValue>
          </TotalSection>
        </ExpandedInner>
      </ExpandedContent>
    </SummaryBarContainer>
  );
};

export default PriceSummaryBar;


